import { useRef, useEffect, useState, useMemo } from 'react';
import DesktopVideoMockup from './DesktopVideoMockup';
import Clapperboard from './Clapperboard';
import StarField from './StarField';
import './Hero.css';
import './phone-sprite.css';

interface HeroProps {
  image?: string;
  showContent?: boolean;
}

export default function Hero({ image, showContent = true }: HeroProps) {
  const desktopRef = useRef<HTMLDivElement>(null);
  const desktopScreenRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [scrollY, setScrollY] = useState(0); // Track scroll position for sticky phones
  const timeOffsetRef = useRef(Date.now()); // Time-based offset for dither (initialized once)
  const [desktopCenter, setDesktopCenter] = useState({ x: 50, y: 50 }); // Desktop center in viewport units
  const [videoStarted, setVideoStarted] = useState(false);
  const videoStartedRef = useRef(false);
  const [isMobile, setIsMobile] = useState(false);

  // Memoize random float animation values so they don't change on re-renders
  const floatConfigs = useMemo(() => {
    // Different animation variants for variety
    const variants = ['float-1', 'float-2', 'float-3', 'float-4', 'float-5', 'float-6'];
    return [1, 2, 3, 4, 5, 6].reduce(
      (acc, index) => {
        acc[index] = {
          variant: variants[index - 1], // Assign different variant to each phone
          duration: 15 + Math.random() * 10, // 15-25s duration (much slower)
          delay: Math.random() * -8, // random start offset
        };
        return acc;
      },
      {} as Record<number, { variant: string; duration: number; delay: number }>
    );
  }, []);

  useEffect(() => {
    videoStartedRef.current = videoStarted;
  }, [videoStarted]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!showContent) return;

    const handleScroll = () => {
      if (!desktopRef.current) return;

      // Track scroll position for sticky phones
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);

      const viewportHeight = window.innerHeight;
      const viewportCenter = viewportHeight * 0.6;

      // Converge phones to the TOP of the desktop mockup
      let targetX = 50; // Center horizontally
      let targetY = 40; // Default to center of viewport

      if (desktopRef.current) {
        // Get the desktop mockup container position
        const rect = desktopRef.current.getBoundingClientRect();
        // Target the TOP of the desktop mockup (not center)
        const desktopTopX = rect.left + rect.width / 2; // Center horizontally
        const desktopTopY = rect.top / 2; // Top edge of desktop mockup

        // Convert to viewport units
        targetX = (desktopTopX / window.innerWidth) * 100;
        targetY = (desktopTopY / window.innerHeight) * 100;
      }

      // Update desktop center position to top of desktop mockup
      setDesktopCenter({ x: targetX, y: targetY });

      // For progress calculation, use desktop SCREEN AREA position (not container)
      // This is the actual video element area, which is what we want to track
      let elementCenter = viewportHeight + 1000; // Default to way above viewport
      let elementTop = viewportHeight + 1000; // Default to way above viewport
      if (desktopScreenRef.current) {
        // Use the screen area element (where video actually is)
        const screenRect = desktopScreenRef.current.getBoundingClientRect();
        elementCenter = screenRect.top + screenRect.height / 2;
        elementTop = screenRect.top; // Top edge of screen area
      } else if (desktopRef.current) {
        // Fallback to container if screen ref not available
        const rect = desktopRef.current.getBoundingClientRect();
        elementCenter = rect.top + rect.height / 2;
        elementTop = rect.top; // Top edge of desktop
      }

      // Animation starts EXACTLY when desktop ENTERS the viewport
      // Desktop enters viewport when its top edge crosses the bottom of viewport (elementTop <= viewportHeight)
      // Animation ends when desktop center reaches viewport center (video starts)
      const animationStart = viewportHeight; // Desktop enters when top edge reaches bottom of viewport
      const animationEnd = viewportCenter; // End when desktop center reaches viewport center (video starts)

      // Calculate progress (0 to 1)
      let progress = 0;

      // Check if desktop has entered viewport (top edge is at or above bottom of viewport)
      const hasEnteredViewport = elementTop <= viewportHeight;

      if (hasEnteredViewport) {
        // Desktop has entered viewport - start animating immediately
        // Calculate progress based on how far the desktop center has moved from entry to center
        if (elementCenter >= animationEnd) {
          // Desktop center is still above or at viewport center - calculate progress
          const distanceFromCenter = elementCenter - animationEnd; // Distance from center
          const totalDistance = animationStart - animationEnd; // Total distance from entry to center
          progress = 1 - distanceFromCenter / totalDistance;
          // Ensure progress is at least a tiny value when desktop enters to trigger animation
          if (progress === 0) {
            progress = 0.001;
          }
        } else {
          // Desktop center has passed viewport center
          progress = 1; // Fully animated (past center - phones converged)
        }
      } else {
        // Desktop hasn't entered viewport yet - phones stay fixed
        progress = 0; // Not started - phones in original positions
      }

      // Clamp between 0 and 1
      progress = Math.max(0, Math.min(1, progress));
      setScrollProgress(progress);

      // Start video when desktop center reaches viewport center
      // elementCenter decreases as we scroll down, so check if it's at or past viewport center
      const isAtOrPastViewportCenter = elementCenter <= viewportCenter;

      if (isAtOrPastViewportCenter && !videoStartedRef.current) {
        videoStartedRef.current = true;
        setVideoStarted(true);
        if (videoRef.current) {
          videoRef.current.play().catch(error => {
            console.error('Error playing video:', error);
          });
        }
      } else if (!isAtOrPastViewportCenter && videoStartedRef.current) {
        // Pause video when scrolling back up above center
        videoStartedRef.current = false;
        setVideoStarted(false);
        if (videoRef.current) {
          videoRef.current.pause();
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showContent, videoStarted]);

  return (
    <section className="hero film-strip">
      <div
        className="hero-background"
        style={image ? { backgroundImage: `url(${image})` } : {}}
      ></div>
      {showContent && (
        <>
          <div className="gradient-overlay-radial"></div>
          <div className="gradient-overlay-top"></div>

          {/* Floating stars for ambience */}
          <StarField starCount={150} containerClassName="hero-stars" />

          {/* Phone mockups positioned around desktop - outside container to use full viewport */}
          <div className="hero-phone-mockups">
            {[1, 2, 3, 4, 5, 6].map(index => {
              const progress = scrollProgress;

              // All phone positions and initial transforms from CSS - consolidated here
              const phoneStyles: {
                [key: number]: {
                  top: string;
                  left?: string;
                  right?: string;
                  initialScale: number;
                  initialRotation: number;
                };
              } = {
                1: { top: '19vh', left: '3vw', initialScale: 0.8, initialRotation: -18 },
                2: { top: '8vh', left: '15vw', initialScale: 1.1, initialRotation: 10 },
                3: { top: '44vh', left: '11vw', initialScale: 0.9, initialRotation: -98 },
                4: { top: '-2vh', right: '11vw', initialScale: 0.95, initialRotation: -105 },
                5: { top: '25vh', right: '3vw', initialScale: 0.85, initialRotation: -9 },
                6: { top: '35vh', right: '15vw', initialScale: 1.05, initialRotation: 12 },
              };

              const phoneStyle = phoneStyles[index];
              // Reduce scale on mobile
              const scaleMultiplier = isMobile ? 0.75 : 1;
              const initialScale = phoneStyle.initialScale * scaleMultiplier;
              const initialRotation = phoneStyle.initialRotation;
              const finalScale = 0.15;
              const currentScale = initialScale + (finalScale - initialScale) * progress;

              // Calculate base rotation (fade from initial to 0)
              let currentRotation = initialRotation * (1 - progress);

              // Add rotation animation when phones are sticky (before convergence)
              // Make rotations larger and more noticeable
              // Ensure rotation direction remains fixed (clockwise or counterclockwise)
              if (progress === 0) {
                // Larger rotation amounts for more visible animation
                // Each phone rotates differently for visual interest
                // Positive = clockwise, Negative = counterclockwise (direction stays fixed)
                const stickyRotations: { [key: number]: number } = {
                  1: 25, // Phone 1: 25 degrees (clockwise) - very obvious movement
                  2: -20, // Phone 2: -20 degrees (counterclockwise) - very obvious
                  3: 18, // Phone 3: 18 degrees (clockwise) - very obvious
                  4: 22, // Phone 4: -22 degrees (counterclockwise) - very obvious
                  5: -20, // Phone 5: -20 degrees (counterclockwise/left) - very obvious
                  6: 20, // Phone 6: 20 degrees (clockwise/right) - very obvious
                };

                // Add dither: combine scroll position and time for variation
                // Make it more responsive to scroll - use smaller divisor and ensure it works at scrollY = 0
                const time = (Date.now() - timeOffsetRef.current) / 1000; // Time in seconds
                // Use scrollY + 1 to ensure we always have some value, even at 0
                // Oscillate between 0.2 and 1.0 to maintain rotation direction and make movement more obvious
                const scrollFactor = Math.sin((scrollY + 1) / 60 + time * 0.5) * 0.4 + 0.6; // Oscillates between 0.2 and 1.0, faster oscillation
                const stickyRotation = stickyRotations[index] || 0;
                // Always rotate in the direction of stickyRotation (never cross zero to change direction)
                // If stickyRotation is positive (clockwise), always add positive rotation
                // If stickyRotation is negative (counterclockwise), always add negative rotation
                const stickyRotationAmount = Math.abs(stickyRotation) * scrollFactor;
                if (stickyRotation > 0) {
                  // Clockwise: add positive rotation, but ensure we don't go less clockwise than initial
                  currentRotation = initialRotation + stickyRotationAmount;
                } else {
                  // Counterclockwise: subtract rotation, but ensure we don't go less counterclockwise than initial
                  currentRotation = initialRotation - stickyRotationAmount;
                }
              }

              // Add tumbling rotation effect for all phones with randomized amounts and directions
              if (progress > 0) {
                // Randomized tumbling rotation amounts and directions for each phone
                // Some rotate a lot, some rotate a little, some clockwise, some counterclockwise
                const tumblingRotations: { [key: number]: number } = {
                  1: 360, // Phone 1: 1 full rotation (clockwise) - max 360
                  2: -540, // Phone 2: 1 full rotation (counterclockwise) - max 360
                  3: 600, // Phone 3: 1 full rotation (clockwise) - max 360
                  4: -450, // Phone 4: 1 full rotation (counterclockwise) - max 360
                  5: -270, // Phone 5: half rotation (counterclockwise/left) - less than 360
                  6: 360, // Phone 6: 1 full rotation (clockwise/right) - max 360
                };

                const tumblingAmount = tumblingRotations[index] || 0;
                const tumblingRotation = progress * tumblingAmount;
                currentRotation = initialRotation * (1 - progress) + tumblingRotation;
              }

              // Make opacity more visible during animation, but completely disappear when video starts
              // When progress >= 1 (video playing), phones should be completely gone
              const opacity =
                progress >= 1 ? 0 : progress === 0 ? 1 : Math.max(0, 1 - progress * 0.8);

              // Adjust positions for mobile
              let adjustedLeft = phoneStyle.left;
              let adjustedRight = phoneStyle.right;
              let adjustedTop = phoneStyle.top;
              
              if (isMobile) {
                if (index === 2) {
                  // Phone 2: move more leftwards on mobile
                  adjustedLeft = '2vw';
                } else if (index === 6) {
                  // Phone 6: move downwards on mobile
                  adjustedTop = '45vh';
                }
              }

              // Calculate position for animation
              let currentXVw: number;
              if (adjustedLeft !== undefined) {
                currentXVw = parseFloat(adjustedLeft);
              } else if (adjustedRight !== undefined) {
                currentXVw = 100 - parseFloat(adjustedRight);
              } else {
                currentXVw = 50;
              }

              const currentYVh = parseFloat(adjustedTop);

              // Make phones sticky: offset by scroll amount to keep them visually fixed to viewport
              // Then add convergence animation when desktop enters viewport
              const stickyOffsetY = scrollY; // Offset by scroll to keep phones visually fixed
              const convergenceOffsetX = (desktopCenter.x - currentXVw) * progress;
              const convergenceOffsetY = (desktopCenter.y - currentYVh) * progress;

              // Transform: sticky offset + convergence animation
              // Convert convergenceOffsetY from vh to pixels for proper mixing with scroll offset
              const convergenceOffsetYPx = (convergenceOffsetY / 100) * window.innerHeight;
              const totalOffsetY = stickyOffsetY + convergenceOffsetYPx;

              // Build transform - ensure sticky rotation is included when progress === 0
              const transformValue =
                progress === 0
                  ? `translate(0, ${stickyOffsetY}px) rotate(${currentRotation}deg) scale(${initialScale})`
                  : `translate(${convergenceOffsetX}vw, ${totalOffsetY}px) scale(${currentScale}) rotate(${currentRotation}deg)`;

              // Map phone indices to sprite class names
              // Use landscape images for phones 3 and 4
              // Use p1, p2, p3 images for phones 1, 5, and 6
              // Phone 2 uses the default phone-mockup.png
              const phoneSpriteClass =
                index === 1
                  ? 'phone-sprite-p1'
                  : index === 2
                    ? 'phone-sprite-default'
                    : index === 3
                      ? 'phone-sprite-l1'
                      : index === 4
                        ? 'phone-sprite-l2'
                        : index === 5
                          ? 'phone-sprite-p2'
                          : index === 6
                            ? 'phone-sprite-p3'
                            : 'phone-sprite-default';

              const isLandscape = index === 3 || index === 4;
              const floatConfig = floatConfigs[index];

              // Hide phones 1 and 5 on small screens
              const shouldHideOnMobile = index === 1 || index === 5;

              return (
                <div
                  key={index}
                  className={`hero-phone-mockup-wrapper hero-phone-${index} ${isLandscape ? 'hero-phone-landscape' : ''} ${shouldHideOnMobile ? 'hero-phone-hide-mobile' : ''}`}
                  style={
                    {
                      position: 'absolute',
                      top: adjustedTop,
                      ...(adjustedLeft ? { left: adjustedLeft } : {}),
                      ...(adjustedRight ? { right: adjustedRight } : {}),
                      opacity: opacity,
                      transform: transformValue,
                      '--base-transform': transformValue,
                      willChange: 'transform, opacity',
                      transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
                      pointerEvents: progress >= 1 ? 'none' : 'auto',
                      zIndex: 2, // Below clapperboard (z-index: 10)
                      display: progress >= 1 ? 'none' : 'block', // Completely hide when animation complete
                    } as React.CSSProperties & { '--base-transform'?: string }
                  }
                >
                  <div
                    className={`hero-phone-float-container hero-phone-float-${floatConfig.variant}`}
                    style={{
                      animationDuration: `${floatConfig.duration}s`,
                      animationDelay: `${floatConfig.delay}s`,
                      // Pause floating animation when phones are converging
                      animationPlayState: progress > 0 ? 'paused' : 'running',
                    }}
                  >
                    <div
                      className={`phone-sprite ${phoneSpriteClass} hero-phone-mockup`}
                      aria-label={`Phone ${index}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hero-content container">
            <h1 className="sr-only">Kids Vids - A Narrative of Your Life</h1>
            <div
              className="hero-clapperboard-wrapper"
              style={{
                // Fade out later in the scroll (start at 0.4, fully gone by 0.7)
                opacity: Math.max(0, 1 - Math.max(0, scrollProgress - 0.4) * 3.3),

                // Rotate board from -2deg to 0deg (flat) during first phase (0 to 0.25)
                // Then keep it flat
                transform: `rotate(${Math.min(0, -2 + scrollProgress * 8)}deg) scale(${Math.max(0.8, 1 - scrollProgress * 0.5)})`,

                transition: 'opacity 0.1s linear, transform 0.1s linear',
              }}
            >
              <Clapperboard autoClap={false} scrollProgress={scrollProgress} />
            </div>
            <a
              href="#desktop-video"
              className="hero-scroll-indicator"
              onClick={e => {
                e.preventDefault();
                const videoElement = document.getElementById('desktop-video');
                if (videoElement) {
                  const viewportHeight = window.innerHeight;
                  const viewportCenter = viewportHeight * 0.6;
                  const videoRect = videoElement.getBoundingClientRect();
                  const videoCenterY = videoRect.top + videoRect.height / 2;
                  // Position slightly above center to ensure video play condition triggers
                  // The video plays when elementCenter <= viewportCenter, so we need to scroll slightly further
                  const offsetAboveCenter = 50; // Increased offset to ensure video plays
                  const targetScrollY =
                    window.scrollY + videoCenterY - (viewportCenter - offsetAboveCenter);

                  // Use native smooth scroll - simpler and works like wheel scroll
                  window.scrollTo({
                    top: targetScrollY,
                    behavior: 'smooth',
                  });

                  // Trigger scroll event after scroll completes to check video play condition
                  setTimeout(() => {
                    window.dispatchEvent(new Event('scroll'));
                  }, 1000);
                }
              }}
            >
              <span className="scroll-text">Scroll</span>
              <div className="scroll-arrow">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 10L12 15L17 10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </a>

            <div className="hero-desktop-mockup" ref={desktopRef}>
              <DesktopVideoMockup
                videoSrc="/kids-vids-hero-clip.mp4"
                autoplay={false}
                loop={true}
                muted={true}
                ref={videoRef}
                screenAreaRef={desktopScreenRef}
              />
            </div>
          </div>
        </>
      )}
    </section>
  );
}
